import { Box, Button, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';

import AddIcon from '@mui/icons-material/Add';
import CreateUserGroupModal from './components/CreateUserGroupModal';
import SearchBar from 'components/SharedComponents/SearchBar/SearchBar';
import StyBoxAdmin from 'routes/Admin/shared/StyBoxAdmin';
import StyGridTitleContainer from 'routes/Admin/shared/StyGridTitleContainer';
import UserGroupsGrid from './UserGroupsGrid/UserGroupsGrid';
import { UserGroupsInformationAlert } from './UserGroupsInformationAlert';
import { checkStringMatch } from 'routes/Library/helpers';
import useGetUserGroups from 'hooks/userGroups/useGetUserGroups';
import { useTranslation } from 'react-i18next';

const StyHeader = styled(Box, {
    name: 'StyHeader',
})(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    flex: 1,
    margin: `0 ${theme.spacing(1)}`,
}));

const StySearchBarContainer = styled(Box, {
    name: 'StySearchBarContainer',
})({
    alignSelf: 'center',
    display: 'flex',
});

const ManageUserGroups = (): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();

    const [isCreateUserGroupModalOpen, setIsCreateUserGroupModalOpen] =
        useState(false);
    const [search, setSearch] = useState('');

    const { data: userGroups = [], isLoading } = useGetUserGroups(true);

    const sortedUserGroups = useMemo(() => {
        const collator = new Intl.Collator('fr');
        return [...userGroups].sort((a, b) => collator.compare(a.name, b.name));
    }, [userGroups]);
    const filteredUserGroups = useMemo(
        () =>
            sortedUserGroups.filter((userGroup) =>
                checkStringMatch(userGroup.name, search),
            ),
        [sortedUserGroups, search],
    );
    const existingGroupsNames = userGroups.map((userGroup) => userGroup.name);
    return (
        <StyBoxAdmin
            sx={{
                p: theme.spacing(5),
                gap: theme.spacing(3),
            }}
        >
            <UserGroupsInformationAlert />
            <StyHeader>
                <StyGridTitleContainer
                    sx={{
                        alignItems: 'baseline',
                        gap: '1em',
                    }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            color: theme.palette.darkGrey,
                            '&:first-letter': {
                                textTransform: 'initial', // otherwise we have a rogue empty space at the end of the text
                            },
                        }}
                    >
                        {t('adminSpace.userGroupsList.title')}
                    </Typography>
                </StyGridTitleContainer>
                <StySearchBarContainer>
                    <SearchBar
                        width="md"
                        onChange={setSearch}
                        placeholder={t(
                            'adminSpace.userGroupsList.searchPlaceholder',
                        )}
                    />
                </StySearchBarContainer>
                <Button
                    className="pendo-create-group-start"
                    sx={{ marginLeft: theme.spacing(2) }}
                    variant="contained"
                    startIcon={<AddIcon fontSize="small" />}
                    onClick={() => setIsCreateUserGroupModalOpen(true)}
                >
                    {t('adminSpace.userGroupsList.addButton')}
                </Button>
            </StyHeader>
            <UserGroupsGrid
                userGroups={filteredUserGroups}
                isLoading={isLoading}
            />
            {isCreateUserGroupModalOpen && (
                <CreateUserGroupModal
                    isOpen={isCreateUserGroupModalOpen}
                    onClose={() => setIsCreateUserGroupModalOpen(false)}
                    existingGroupsNames={existingGroupsNames}
                />
            )}
        </StyBoxAdmin>
    );
};

export default ManageUserGroups;
